import React, { useState } from 'react';
import { motion } from 'framer-motion';
import PageTransition from '../components/PageTransition';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  MenuItem,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
    preferredDate: '',
    preferredTime: '',
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    setSnackbar({
      open: true,
      message: 'Thank you for your message. We will contact you soon!',
      severity: 'success',
    });
    setFormData({
      name: '',
      email: '',
      phone: '',
      service: '',
      message: '',
      preferredDate: '',
      preferredTime: '',
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const services = [
    'TV Mounting & Installation',
    'Smart TV Setup',
    'Universal Remote Setup',
    'Home Theater Installation',
    'Other',
  ];

  const timeSlots = [
    '9:00 AM - 11:00 AM',
    '11:00 AM - 1:00 PM',
    '2:00 PM - 4:00 PM',
    '4:00 PM - 6:00 PM',
  ];

  const contactInfo = [
    {
      icon: <PhoneIcon color="primary" />,
      title: 'Phone',
      content: '(815) 625-8453',
      link: 'tel:+18156258453',
    },
    {
      icon: <EmailIcon color="primary" />,
      title: 'Email',
      content: 'info@wisetvsetup.info',
      link: 'mailto:info@wisetvsetup.info',
    },
    {
      icon: <LocationOnIcon color="primary" />,
      title: 'Address',
      content: '311 E 29th St, Sterling, Illinois(IL), 61081',
      link: 'https://maps.google.com',
    },
    {
      icon: <AccessTimeIcon color="primary" />,
      title: 'Business Hours',
      content: 'Mon - Sat: 9:00 AM - 6:00 PM',
    },
  ];

  const serviceAreas = [
    'Tech City Central',
    'West End District',
    'North Hills Area',
    'South Bay Region',
    'East Side Community',
    'Downtown Metro',
  ];

  const emergencyServices = {
    phone: '(815) 625-8453',
    hours: '24/7 Emergency Support',
    response: 'Average response time: 30 minutes',
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerChildren = {
    animate: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const cardHover = {
    scale: 1.02,
    transition: { duration: 0.2 }
  };

  return (
    <PageTransition>
      <Box>
        {/* Contact Header */}
        <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 8 }}>
          <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
            <motion.div {...fadeInUp}>
              <Typography 
                variant="h2" 
                component="h1" 
                gutterBottom 
                sx={{ 
                  textAlign: 'center',
                  fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
                  fontWeight: 700,
                  mb: 3,
                  color: 'white'
                }}
              >
                Contact Us
              </Typography>
            </motion.div>
            <motion.div {...fadeInUp}>
              <Typography 
                variant="h5" 
                sx={{ 
                  mb: 4,
                  textAlign: 'center',
                  maxWidth: '900px',
                  margin: '0 auto',
                  opacity: 0.9,
                  fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.6rem' },
                  lineHeight: 1.4,
                }}
              >
                Get in touch for professional TV setup services
              </Typography>
            </motion.div>
          </Container>
        </Box>

        <Container sx={{ py: 8 }}>
          <Grid container spacing={4}>
            {/* Contact Form */}
            <Grid item xs={12} md={7}>
              <motion.div {...fadeInUp}>
                <Paper sx={{ p: 4 }}>
                  <Typography variant="h4" component="h2" gutterBottom>
                    Schedule a Service
                  </Typography>
                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          required
                          fullWidth
                          label="Name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          required
                          fullWidth
                          label="Email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          required
                          fullWidth
                          label="Phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          required
                          fullWidth
                          select
                          label="Service"
                          name="service"
                          value={formData.service}
                          onChange={handleChange}
                        >
                          {services.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Preferred Date"
                          name="preferredDate"
                          type="date"
                          value={formData.preferredDate}
                          onChange={handleChange}
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          select
                          label="Preferred Time"
                          name="preferredTime"
                          value={formData.preferredTime}
                          onChange={handleChange}
                        >
                          {timeSlots.map((slot) => (
                            <MenuItem key={slot} value={slot}>
                              {slot}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          required
                          fullWidth
                          multiline
                          rows={4}
                          label="Message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Please provide additional details about your TV setup needs"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          fullWidth
                        >
                          Send Message
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                </Paper>
              </motion.div>
            </Grid>

            {/* Contact Information */}
            <Grid item xs={12} md={5}>
              <Grid container spacing={3}>
                {/* Contact Cards */}
                <motion.div variants={staggerChildren} initial="initial" animate="animate">
                  {contactInfo.map((info, index) => (
                    <Grid item xs={12} key={index}>
                      <motion.div variants={fadeInUp}>
                        <motion.div whileHover={cardHover}>
                          <Card>
                            <CardContent>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                {info.icon}
                                <Box>
                                  <Typography variant="h6" gutterBottom>
                                    {info.title}
                                  </Typography>
                                  {info.link ? (
                                    <Typography
                                      component="a"
                                      href={info.link}
                                      color="primary"
                                      sx={{ textDecoration: 'none' }}
                                    >
                                      {info.content}
                                    </Typography>
                                  ) : (
                                    <Typography color="text.secondary">
                                      {info.content}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        </motion.div>
                      </motion.div>
                    </Grid>
                  ))}

                  {/* Emergency Services */}
                  <Grid item xs={12}>
                    <motion.div variants={fadeInUp}>
                      <motion.div whileHover={cardHover}>
                        <Card sx={{ bgcolor: 'error.main', color: 'white' }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                              <PriorityHighIcon />
                              <Typography variant="h6">Emergency Services</Typography>
                            </Box>
                            <Typography variant="body1" sx={{ mb: 1 }}>
                              Emergency Phone: {emergencyServices.phone}
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 1 }}>
                              {emergencyServices.hours}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                              {emergencyServices.response}
                            </Typography>
                          </CardContent>
                        </Card>
                      </motion.div>
                    </motion.div>
                  </Grid>
                </motion.div>
              </Grid>
            </Grid>
          </Grid>
        </Container>

        {/* Service Area Section */}
        <Box sx={{ bgcolor: 'grey.100', py: 8 }}>
          <Container>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <motion.div {...fadeInUp}>
                  <Typography variant="h4" gutterBottom>
                    Our Service Areas
                  </Typography>
                  <Typography variant="body1" paragraph color="text.secondary">
                    We provide TV setup and installation services in the following areas:
                  </Typography>
                  <motion.div variants={staggerChildren} initial="initial" animate="animate">
                    <List>
                      {serviceAreas.map((area, index) => (
                        <motion.div key={index} variants={fadeInUp}>
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText primary={area} />
                          </ListItem>
                        </motion.div>
                      ))}
                    </List>
                  </motion.div>
                </motion.div>
              </Grid>
              <Grid item xs={12} md={6}>
                <motion.div {...fadeInUp}>
                  <motion.div whileHover={cardHover}>
                    <Card>
                      <CardContent>
                        <Typography variant="h5" gutterBottom>
                          Service Highlights
                        </Typography>
                        <List>
                          <ListItem>
                            <ListItemIcon>
                              <LocalShippingIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                              primary="Same Day Service Available"
                              secondary="For orders placed before 11 AM"
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon>
                              <SupportAgentIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                              primary="Expert Support"
                              secondary="Certified technicians for all services"
                            />
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </motion.div>
                </motion.div>
              </Grid>
            </Grid>
          </Container>
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </PageTransition>
  );
};

export default Contact; 